/**
 * Supabase 전송량 모니터(TopNav 중앙).
 *
 * TopNav 「오늘 보낸/받은」은 **PostgREST(`/rest/v1`)만** 집계해 대시보드 DB egress에 가깝게 맞춥니다.
 * - 받은 ≈ REST 응답 와이어 용량(가능하면 transferSize / Content-Length)
 * - 보낸 = REST 요청 본문(업로드·upsert; egress가 아님)
 * Auth·Storage·Functions·브라우저 HTTP 캐시·localStorage는 보낸/받은에 넣지 않습니다.
 * 오늘 누적·직전 구간은 localStorage + BroadcastChannel로 **탭 간 공유**됩니다
 * (메인 프레젠·슬라이드 탭 활동을 메인 TopNav에서도 합산).
 * 수동 점검 시나리오는 `dataTransferAudit.ts` / `REACT_QUERY_EGRESS_CHECKLIST`를 참고합니다.
 */

const isDev = process.env.NODE_ENV === 'development';

/** v2: REST-only + opaque 과대계상 완화 — 이전 누적과 섞이지 않게 키 변경 */
const DAILY_USAGE_LS_KEY = 'cp.dataUsage.daily.v2';
/** 다른 탭의「지금」구간 표시를 메인 TopNav에 공유 */
const BURST_USAGE_LS_KEY = 'cp.dataUsage.burst.v2';
const CROSS_TAB_CHANNEL_NAME = 'cp.dataUsage.sync.v2';

type DailyUsageStore = {
  date: string;
  sentBytes: number;
  receivedBytes: number;
  localReadCount: number;
  localReadBytes: number;
};

type BurstSharedStore = {
  date: string;
  friendly: string;
  technical: string;
  updatedAt: number;
};

type CrossTabMessage =
  | { type: 'daily-changed' }
  | { type: 'daily-reset' }
  | { type: 'burst-changed'; friendly: string; technical: string; updatedAt: number };

const PROGRAM_REST_TABLES = new Set([
  'program_progress_cells',
  'program_fixed_content_rows',
  'program_scoped_content_rows',
]);

export type DevNetworkTransferSnapshot = {
  fetchCount: number;
  restV1Count: number;
  requestBytes: number;
  responseBytes: number;
  /** program_* upsert(POST 등)로 보낸 행 수 합계 */
  programUpsertPayloadRows: number;
  /** program_* upsert 호출 수 */
  programUpsertCalls: number;
  /** program_* GET 응답 JSON 배열 행 수 합계 */
  programGetPayloadRows: number;
  programGetCalls: number;
  /** 동일 테이블 최근 GET 배열 길이(upsert 비교 분모용) */
  lastProgramGetRowsByTable: Record<string, number>;
  /** 직전 program_* upsert 1건(분자/분모 표시용) */
  lastProgramUpsert: null | {
    table: string;
    sentRows: number;
    /** 해당 테이블에 대한 이전 GET 배열 길이(없으면 null) */
    denomRows: number | null;
  };
  /** 브라우저 HTTP 캐시로만 응답된 Supabase REST fetch 횟수 */
  browserHttpCacheRestCount: number;
  /** 위와 동일, REST가 아닌 fetch */
  browserHttpCacheOtherCount: number;
  /** 로컬스토리지(앱) 프로그램 관련 캐시 적중 횟수 — `recordDevLocalStorageProgramCacheUse` */
  localStorageProgramCacheReads: number;
  /** 로컬스토리지 읽기 추정 용량(바이트) */
  localStorageReadBytes: number;
};

const snapshot: DevNetworkTransferSnapshot = {
  fetchCount: 0,
  restV1Count: 0,
  requestBytes: 0,
  responseBytes: 0,
  programUpsertPayloadRows: 0,
  programUpsertCalls: 0,
  programGetPayloadRows: 0,
  programGetCalls: 0,
  lastProgramGetRowsByTable: {},
  lastProgramUpsert: null,
  browserHttpCacheRestCount: 0,
  browserHttpCacheOtherCount: 0,
  localStorageProgramCacheReads: 0,
  localStorageReadBytes: 0,
};

const listeners = new Set<() => void>();

/** 직전 유휴 구간(디바운스) 요약 — 비어 있으면 TopNav에서 플레셀홀더, 갱신 시에만 덮어씀(사라지지 않음) */
let lastBurstDisplay = '';
let lastBurstDisplayFriendly = '';

type BurstSlice = {
  fetchCount: number;
  restV1Count: number;
  requestBytes: number;
  responseBytes: number;
  programUpsertPayloadRows: number;
  programUpsertCalls: number;
  programGetPayloadRows: number;
  programGetCalls: number;
  lastProgramUpsert: DevNetworkTransferSnapshot['lastProgramUpsert'];
  httpCacheRest: number;
  httpCacheOther: number;
  localStorageReads: number;
  localStorageReadBytes: number;
  localStorageLastScope: string;
  /** 개발용: 직전 버스트 구간 라벨(예: 프레젠테이션 열기) */
  scopeLabel: string;
};

const emptyBurst = (): BurstSlice => ({
  fetchCount: 0,
  restV1Count: 0,
  requestBytes: 0,
  responseBytes: 0,
  programUpsertPayloadRows: 0,
  programUpsertCalls: 0,
  programGetPayloadRows: 0,
  programGetCalls: 0,
  lastProgramUpsert: null,
  httpCacheRest: 0,
  httpCacheOther: 0,
  localStorageReads: 0,
  localStorageReadBytes: 0,
  localStorageLastScope: '',
  scopeLabel: '',
});

let burst = emptyBurst();
let burstFlushTimer: ReturnType<typeof setTimeout> | null = null;
const BURST_FLUSH_MS = 220;

function flushBurstToLastLine() {
  burstFlushTimer = null;
  const b = burst;
  burst = emptyBurst();
  const text = formatBurstSliceSummary(b);
  const friendly = formatBurstSliceUserFriendly(b);
  if (text) {
    lastBurstDisplay = text;
    lastBurstDisplayFriendly = friendly;
    publishBurstChanged();
    notify();
  }
}

function scheduleBurstFlush() {
  if (burstFlushTimer != null) clearTimeout(burstFlushTimer);
  burstFlushTimer = setTimeout(flushBurstToLastLine, BURST_FLUSH_MS);
}

function bumpBurst(mutate: (b: BurstSlice) => void) {
  mutate(burst);
  scheduleBurstFlush();
  notify();
}

function formatBurstSliceSummary(b: BurstSlice): string {
  if (
    b.fetchCount === 0 &&
    b.restV1Count === 0 &&
    b.requestBytes === 0 &&
    b.responseBytes === 0 &&
    b.programUpsertCalls === 0 &&
    b.programGetCalls === 0 &&
    b.httpCacheRest === 0 &&
    b.httpCacheOther === 0 &&
    b.localStorageReads === 0
  ) {
    return '';
  }
  const cacheBits: string[] = [];
  if (b.httpCacheRest > 0 || b.httpCacheOther > 0) {
    if (b.httpCacheRest > 0 && b.httpCacheOther > 0) {
      cacheBits.push(`브라우저 HTTP캐시 REST ${b.httpCacheRest}·기타 ${b.httpCacheOther}`);
    } else if (b.httpCacheRest > 0) {
      cacheBits.push(`브라우저 HTTP캐시 REST ${b.httpCacheRest}회`);
    } else {
      cacheBits.push(`브라우저 HTTP캐시 기타 ${b.httpCacheOther}회`);
    }
  }
  if (b.localStorageReads > 0) {
    const scope = (b.localStorageLastScope ?? '').trim() || '프로그램';
    cacheBits.push(`로컬 ${scope} ${b.localStorageReads}회`);
  }
  const cacheLine = cacheBits.join(' · ');
  const scopePrefix = (b.scopeLabel ?? '').trim();
  const up = b.programUpsertPayloadRows;
  const upc = b.programUpsertCalls;
  const ld = b.programGetPayloadRows;
  const ldc = b.programGetCalls;
  const last = b.lastProgramUpsert;
  const ratio =
    last && last.denomRows != null && last.denomRows > 0
      ? `${shortProgramTable(last.table)}↑${last.sentRows}/${last.denomRows}행`
      : last && last.sentRows > 0
        ? `${shortProgramTable(last.table)}↑${last.sentRows}행`
        : '';
  const prog =
    upc > 0 || ldc > 0 ? `저장 ${up}행·${upc}회 · 로드 ${ld}행·${ldc}회` : '';
  const netBits = [
    b.restV1Count > 0 || b.requestBytes > 0 || b.responseBytes > 0
      ? `↑${formatBytes(b.requestBytes)} ↓${formatBytes(b.responseBytes)}`
      : '',
    b.restV1Count > 0 ? `REST ${b.restV1Count}회` : '',
    b.fetchCount > b.restV1Count ? `fetch ${b.fetchCount}회` : '',
    ratio,
    prog,
  ].filter(Boolean);
  const netLine = netBits.join(' · ');
  const scopedNetLine = scopePrefix && netLine ? `${scopePrefix} · ${netLine}` : netLine;
  /** 같은 유휴 구간 안의 로컬/브라우저 캐시와 실제 네트워크를 헷갈리지 않게 구분 */
  if (cacheLine && scopedNetLine) return `${cacheLine} — 네트 ${scopedNetLine}`;
  if (cacheLine) return cacheLine;
  if (scopePrefix && scopedNetLine) return scopedNetLine;
  return scopedNetLine;
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

export function getDevNetworkTransferSnapshot(): DevNetworkTransferSnapshot {
  return { ...snapshot, lastProgramGetRowsByTable: { ...snapshot.lastProgramGetRowsByTable } };
}

export function subscribeDevNetworkTransfer(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function resetDevNetworkTransferStats(): void {
  if (burstFlushTimer != null) {
    clearTimeout(burstFlushTimer);
    burstFlushTimer = null;
  }
  burst = emptyBurst();
  lastBurstDisplay = '';
  lastBurstDisplayFriendly = '';
  snapshot.fetchCount = 0;
  snapshot.restV1Count = 0;
  snapshot.requestBytes = 0;
  snapshot.responseBytes = 0;
  snapshot.programUpsertPayloadRows = 0;
  snapshot.programUpsertCalls = 0;
  snapshot.programGetPayloadRows = 0;
  snapshot.programGetCalls = 0;
  snapshot.lastProgramGetRowsByTable = {};
  snapshot.lastProgramUpsert = null;
  snapshot.browserHttpCacheRestCount = 0;
  snapshot.browserHttpCacheOtherCount = 0;
  snapshot.localStorageProgramCacheReads = 0;
  snapshot.localStorageReadBytes = 0;
  dailyUsage = emptyDailyStore();
  saveDailyUsageStore(dailyUsage);
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(BURST_USAGE_LS_KEY);
    }
  } catch {
    /* ignore */
  }
  try {
    crossTabChannel?.postMessage({ type: 'daily-reset' } satisfies CrossTabMessage);
  } catch {
    /* ignore */
  }
  publishDailyChanged();
  publishBurstChanged();
  notify();
}

/** 모니터 uninstall용 — 세션 메모리만 비우고, 오늘 누적 LS/다른 탭은 건드리지 않음 */
function clearSessionTransferSnapshotOnly(): void {
  if (burstFlushTimer != null) {
    clearTimeout(burstFlushTimer);
    burstFlushTimer = null;
  }
  burst = emptyBurst();
  snapshot.fetchCount = 0;
  snapshot.restV1Count = 0;
  snapshot.requestBytes = 0;
  snapshot.responseBytes = 0;
  snapshot.programUpsertPayloadRows = 0;
  snapshot.programUpsertCalls = 0;
  snapshot.programGetPayloadRows = 0;
  snapshot.programGetCalls = 0;
  snapshot.lastProgramGetRowsByTable = {};
  snapshot.lastProgramUpsert = null;
  snapshot.browserHttpCacheRestCount = 0;
  snapshot.browserHttpCacheOtherCount = 0;
  snapshot.localStorageProgramCacheReads = 0;
  snapshot.localStorageReadBytes = 0;
  // lastBurstDisplay* · dailyUsage LS는 유지(다른 탭 TopNav 누적 보호)
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)}KB`;
  return `${(n / (1024 * 1024)).toFixed(2)}MB`;
}

function formatBytesFriendly(n: number): string {
  if (n <= 0) return '0';
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))}KB`;
  return `${(n / (1024 * 1024)).toFixed(1)}MB`;
}

function todayDateKey(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function emptyDailyStore(date = todayDateKey()): DailyUsageStore {
  return { date, sentBytes: 0, receivedBytes: 0, localReadCount: 0, localReadBytes: 0 };
}

function loadDailyUsageStore(): DailyUsageStore {
  if (typeof window === 'undefined') return emptyDailyStore();
  try {
    const raw = window.localStorage.getItem(DAILY_USAGE_LS_KEY);
    if (!raw) return emptyDailyStore();
    const parsed = JSON.parse(raw) as DailyUsageStore;
    if (!parsed || parsed.date !== todayDateKey()) return emptyDailyStore();
    return {
      date: parsed.date,
      sentBytes: Math.max(0, Number(parsed.sentBytes) || 0),
      receivedBytes: Math.max(0, Number(parsed.receivedBytes) || 0),
      localReadCount: Math.max(0, Number(parsed.localReadCount) || 0),
      localReadBytes: Math.max(0, Number(parsed.localReadBytes) || 0),
    };
  } catch {
    return emptyDailyStore();
  }
}

function saveDailyUsageStore(store: DailyUsageStore): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DAILY_USAGE_LS_KEY, JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

let dailyUsage = loadDailyUsageStore();

let crossTabChannel: BroadcastChannel | null = null;
let crossTabStorageInstalled = false;
let applyingRemoteSync = false;

function publishDailyChanged() {
  if (typeof window === 'undefined' || applyingRemoteSync) return;
  try {
    crossTabChannel?.postMessage({ type: 'daily-changed' } satisfies CrossTabMessage);
  } catch {
    /* ignore */
  }
}

function publishBurstChanged() {
  if (typeof window === 'undefined' || applyingRemoteSync) return;
  const payload: BurstSharedStore = {
    date: todayDateKey(),
    friendly: lastBurstDisplayFriendly,
    technical: lastBurstDisplay,
    updatedAt: Date.now(),
  };
  try {
    window.localStorage.setItem(BURST_USAGE_LS_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
  try {
    crossTabChannel?.postMessage({
      type: 'burst-changed',
      friendly: payload.friendly,
      technical: payload.technical,
      updatedAt: payload.updatedAt,
    } satisfies CrossTabMessage);
  } catch {
    /* ignore */
  }
}

function applyRemoteDailyFromStorage() {
  applyingRemoteSync = true;
  try {
    const remote = loadDailyUsageStore();
    const local =
      dailyUsage.date === todayDateKey() ? dailyUsage : emptyDailyStore();
    if (remote.date !== todayDateKey()) {
      dailyUsage = local;
      notify();
      return;
    }
    // 다른 탭의 잘못된 0 덮어쓰기·경합으로 누적이 줄지 않게 큰 쪽을 채택
    const merged: DailyUsageStore = {
      date: remote.date,
      sentBytes: Math.max(local.sentBytes, remote.sentBytes),
      receivedBytes: Math.max(local.receivedBytes, remote.receivedBytes),
      localReadCount: Math.max(local.localReadCount, remote.localReadCount),
      localReadBytes: Math.max(local.localReadBytes, remote.localReadBytes),
    };
    dailyUsage = merged;
    if (
      merged.sentBytes > remote.sentBytes ||
      merged.receivedBytes > remote.receivedBytes ||
      merged.localReadCount > remote.localReadCount ||
      merged.localReadBytes > remote.localReadBytes
    ) {
      saveDailyUsageStore(merged);
    }
    notify();
  } finally {
    applyingRemoteSync = false;
  }
}

function applyRemoteBurst(friendly: string, technical: string) {
  applyingRemoteSync = true;
  try {
    // 빈 버스트로 기존「지금」줄을 지우지 않음
    if (!friendly && !technical) return;
    if (friendly) lastBurstDisplayFriendly = friendly;
    if (technical) lastBurstDisplay = technical;
    notify();
  } finally {
    applyingRemoteSync = false;
  }
}

function loadBurstSharedStore(): BurstSharedStore | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BURST_USAGE_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BurstSharedStore;
    if (!parsed || parsed.date !== todayDateKey()) return null;
    return {
      date: parsed.date,
      friendly: String(parsed.friendly ?? ''),
      technical: String(parsed.technical ?? ''),
      updatedAt: Number(parsed.updatedAt) || 0,
    };
  } catch {
    return null;
  }
}

function installCrossTabUsageSync(): () => void {
  if (typeof window === 'undefined' || crossTabStorageInstalled) {
    return () => {};
  }
  crossTabStorageInstalled = true;

  try {
    crossTabChannel = new BroadcastChannel(CROSS_TAB_CHANNEL_NAME);
    crossTabChannel.onmessage = (ev: MessageEvent<CrossTabMessage>) => {
      const msg = ev.data;
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'daily-reset') {
        applyingRemoteSync = true;
        try {
          dailyUsage = emptyDailyStore();
          lastBurstDisplay = '';
          lastBurstDisplayFriendly = '';
          notify();
        } finally {
          applyingRemoteSync = false;
        }
        return;
      }
      if (msg.type === 'daily-changed') {
        applyRemoteDailyFromStorage();
        return;
      }
      if (msg.type === 'burst-changed') {
        applyRemoteBurst(msg.friendly ?? '', msg.technical ?? '');
      }
    };
  } catch {
    crossTabChannel = null;
  }

  const onStorage = (e: StorageEvent) => {
    if (e.storageArea && e.storageArea !== window.localStorage) return;
    if (e.key === DAILY_USAGE_LS_KEY) {
      applyRemoteDailyFromStorage();
      return;
    }
    if (e.key === BURST_USAGE_LS_KEY) {
      const shared = loadBurstSharedStore();
      if (shared) applyRemoteBurst(shared.friendly, shared.technical);
    }
  };
  window.addEventListener('storage', onStorage);

  // 늦게 열린 메인 탭이 직전 버스트를 볼 수 있게
  const shared = loadBurstSharedStore();
  if (shared?.friendly) {
    lastBurstDisplayFriendly = shared.friendly;
    lastBurstDisplay = shared.technical;
  }

  return () => {
    window.removeEventListener('storage', onStorage);
    try {
      crossTabChannel?.close();
    } catch {
      /* ignore */
    }
    crossTabChannel = null;
    crossTabStorageInstalled = false;
  };
}

/**
 * 다른 탭과 경합해도 누락되지 않게 LS를 다시 읽은 뒤 가산한다.
 */
function mutateDailyUsage(mutate: (store: DailyUsageStore) => void): void {
  const store = loadDailyUsageStore();
  mutate(store);
  dailyUsage = store;
  saveDailyUsageStore(store);
  publishDailyChanged();
  notify();
}

function addDailySent(bytes: number): void {
  if (bytes <= 0) return;
  mutateDailyUsage((store) => {
    store.sentBytes += bytes;
  });
}

function addDailyReceived(bytes: number): void {
  if (bytes <= 0) return;
  mutateDailyUsage((store) => {
    store.receivedBytes += bytes;
  });
}

function addDailyLocalRead(count: number, bytes: number): void {
  if (count <= 0 && bytes <= 0) return;
  mutateDailyUsage((store) => {
    store.localReadCount += count;
    store.localReadBytes += bytes;
  });
}

function humanActionLabel(raw: string): string {
  const s = (raw ?? '').trim();
  if (!s) return '';
  if (s.includes('프레젠')) return '프레젠테이션';
  if (s.includes('행동')) return '행동특성';
  if (s.includes('연간') && s.includes('진도')) return '연간진도표';
  if (s.includes('연간') && s.includes('시간')) return '연간시간표';
  if (s.includes('대시보드')) return '대시보드';
  if (s.includes('ToDo') || s.includes('할 일')) return '할 일';
  if (s.includes('예산')) return '예산';
  if (s.includes('학습') && s.includes('내용')) return '학습내용';
  if (s.includes('프로그램') || s === '캐시') return '수업 기록';
  return s.replace(/·LS$/i, '').replace(/\s*LS$/i, '').trim();
}

function formatBurstSliceUserFriendly(b: BurstSlice): string {
  if (
    b.fetchCount === 0 &&
    b.restV1Count === 0 &&
    b.requestBytes === 0 &&
    b.responseBytes === 0 &&
    b.localStorageReads === 0
  ) {
    return '';
  }
  if (b.requestBytes === 0 && b.responseBytes === 0 && b.localStorageReads === 0) {
    return '';
  }
  const action = humanActionLabel(b.scopeLabel || b.localStorageLastScope);
  const parts: string[] = ['지금'];
  if (action) parts.push(action);
  if (b.requestBytes > 0 || b.responseBytes > 0) {
    const netParts: string[] = [];
    if (b.requestBytes > 0) netParts.push(`보냄 ${formatBytesFriendly(b.requestBytes)}`);
    if (b.responseBytes > 0) netParts.push(`받음 ${formatBytesFriendly(b.responseBytes)}`);
    parts.push(`DB ${netParts.join(' · ')}`);
  }
  if (b.localStorageReads > 0) {
    if (b.localStorageReadBytes > 0) {
      parts.push(`기기 저장 ${formatBytesFriendly(b.localStorageReadBytes)}`);
    } else {
      parts.push(`기기 저장 ${b.localStorageReads}회`);
    }
  }
  return parts.join(' · ');
}

/** TopNav — 오늘 하루 누적(사용자용) */
export function formatUserDailyDataUsageSummary(): string {
  const d = dailyUsage.date === todayDateKey() ? dailyUsage : emptyDailyStore();
  const parts: string[] = [];
  parts.push(`보낸 ${formatBytesFriendly(d.sentBytes)}`);
  parts.push(`받은 ${formatBytesFriendly(d.receivedBytes)}`);
  if (d.localReadBytes > 0) {
    parts.push(`기기 저장 ${formatBytesFriendly(d.localReadBytes)}`);
  } else if (d.localReadCount > 0) {
    parts.push(`기기 저장 ${d.localReadCount}회`);
  }
  return `오늘 ${parts.join(' · ')}`;
}

/** TopNav — 직전·진행 중 구간(사용자용) */
export function formatUserRecentDataUsageDisplay(): string {
  const live = formatBurstSliceUserFriendly(burst);
  if (live) return live;
  if (lastBurstDisplayFriendly) return lastBurstDisplayFriendly;
  return '지금 · 대기 중';
}

export function getUserDailyDataUsageStore(): DailyUsageStore {
  return dailyUsage.date === todayDateKey() ? { ...dailyUsage } : emptyDailyStore();
}

/** 다른 탭에서 갱신된 LS 오늘 사용량·버스트를 이 탭 메모리에 반영 */
export function syncDevNetworkTransferFromStorage(): void {
  dailyUsage = loadDailyUsageStore();
  const shared = loadBurstSharedStore();
  if (shared?.friendly) {
    lastBurstDisplayFriendly = shared.friendly;
    lastBurstDisplay = shared.technical;
  }
  notify();
}

function shortProgramTable(table: string): string {
  if (table === 'program_progress_cells') return '진도';
  if (table === 'program_fixed_content_rows') return '고정';
  if (table === 'program_scoped_content_rows') return '스코프';
  return table;
}

/** TopNav 한 줄 요약 */
export function formatDevNetworkTransferSummary(): string {
  const s = snapshot;
  const up = s.programUpsertPayloadRows;
  const upc = s.programUpsertCalls;
  const ld = s.programGetPayloadRows;
  const ldc = s.programGetCalls;
  const last = s.lastProgramUpsert;
  const ratio =
    last && last.denomRows != null && last.denomRows > 0
      ? `${shortProgramTable(last.table)}저장 ${last.sentRows}/${last.denomRows}행`
      : last && last.sentRows > 0
        ? `${shortProgramTable(last.table)}저장 ${last.sentRows}행`
        : '';
  const cum =
    upc > 0 || ldc > 0
      ? `Σ저장 ${up}행/${upc}회 · Σ로드 ${ld}행/${ldc}회`
      : '';
  const bits = [
    `dev ↑${formatBytes(s.requestBytes)} ↓${formatBytes(s.responseBytes)}`,
    `REST ${s.restV1Count}회`,
    ratio,
    cum,
  ].filter(Boolean);
  return bits.join(' · ') || 'dev 네트워크 · 대기';
}

/**
 * 노란 줄용: 진행 중인 버스트(아직 유휴 전)가 있으면 그 값, 없으면 직전 확정 구간.
 * 둘 다 비면 TopNav에서 플레이스홀더.
 */
export function getDevNetworkTransferBurstDisplay(): string {
  const live = formatBurstSliceSummary(burst);
  if (live) return live;
  return lastBurstDisplay;
}

/** 네트워크 전송·HTTP캐시·로컬 캐시 중 하나라도 있으면 TopNav에 개발 블록 표시 */
export function hasDevNetworkTransferActivity(): boolean {
  return (
    snapshot.fetchCount > 0 ||
    snapshot.restV1Count > 0 ||
    snapshot.requestBytes > 0 ||
    snapshot.responseBytes > 0 ||
    snapshot.browserHttpCacheRestCount > 0 ||
    snapshot.browserHttpCacheOtherCount > 0 ||
    snapshot.localStorageProgramCacheReads > 0
  );
}

/**
 * localStorage 등 앱 캐시로 서버 fetch를 생략한 경우(개발 모드만).
 * 서비스/훅에서 캐시 적중 시 1회 호출.
 */
export function recordDevLocalStorageProgramCacheUse(scope: string, estimatedBytes = 0): void {
  if (typeof window === 'undefined') return;
  const bytes = Math.max(0, estimatedBytes);
  snapshot.localStorageProgramCacheReads += 1;
  snapshot.localStorageReadBytes += bytes;
  addDailyLocalRead(1, bytes);
  const label = (scope ?? '').trim() || '캐시';
  bumpBurst((b) => {
    b.localStorageReads += 1;
    b.localStorageReadBytes += bytes;
    b.localStorageLastScope = label;
  });
}

/** 프레젠테이션 열기 직전 호출 — 이후 REST가 TopNav 버스트에「프레젠테이션 열기」로 표시됨 */
export function markDevPresentationOpenTransferScope(): void {
  if (typeof window === 'undefined') return;
  bumpBurst((b) => {
    b.scopeLabel = '프레젠테이션 열기';
  });
}

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

type ResourceTimingEntry = PerformanceResourceTiming & { deliveryType?: string };

function findLatestFetchResourceTiming(resourceUrl: string): ResourceTimingEntry | null {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByName !== 'function') {
    return null;
  }
  try {
    const name = String(resourceUrl);
    const entries = performance.getEntriesByName(name) as ResourceTimingEntry[];
    for (let j = entries.length - 1; j >= 0; j -= 1) {
      const e = entries[j];
      if (e.initiatorType === 'fetch' || e.initiatorType === 'xmlhttprequest') return e;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * PostgREST JSON은 보통 gzip으로 내려오므로, opaque일 때 비압축 본문 길이 대신
 * 와이어(egress)에 가깝게 압축률을 가정한다.
 */
export function estimateGzipWireBytesFromDecodedBody(decodedBodyBytes: number): number {
  const n = Math.max(0, decodedBodyBytes);
  if (n <= 0) return 0;
  // 작은 응답은 헤더·프레임 비중이 커서 압축 이득이 적음
  if (n < 512) return n;
  return Math.max(64, Math.round(n * 0.35));
}

function parseContentLengthBytes(headers: Headers | null | undefined): number {
  if (!headers) return 0;
  const raw = headers.get('content-length');
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function resourceTimingSpanMs(entry: ResourceTimingEntry): number {
  if (entry.responseEnd > 0 && entry.fetchStart > 0) {
    return entry.responseEnd - entry.fetchStart;
  }
  return entry.duration;
}

/**
 * PerformanceResourceTiming + Content-Length로 REST egress(받음)를 추정한다.
 * - deliveryType=cache / transferSize=0+decodedBodySize>0 → HTTP 캐시(제외)
 * - transferSize>0 → 와이어 용량(가장 egress에 가까움)
 * - Content-Length → 압축 후 본문 길이(차선)
 * - opaque: 짧은 span은 캐시, 아니면 gzip 추정(비압축 본문 전량 사용 금지)
 */
export function classifyFetchNetworkTransfer(
  entry: ResourceTimingEntry | null,
  responseBodyBytes: number,
  contentLengthBytes = 0
): { fromCache: boolean; receivedBytes: number } {
  const body = Math.max(0, responseBodyBytes);
  const cl = Math.max(0, contentLengthBytes);

  if (!entry) {
    if (cl > 0) return { fromCache: false, receivedBytes: cl };
    return { fromCache: false, receivedBytes: estimateGzipWireBytesFromDecodedBody(body) };
  }
  if (entry.deliveryType === 'cache') {
    return { fromCache: true, receivedBytes: 0 };
  }
  if (entry.transferSize > 0) {
    return { fromCache: false, receivedBytes: entry.transferSize };
  }
  // 동일 출처·TAO 허용: 캐시 히트는 transferSize=0 이고 decodedBodySize>0
  if (entry.decodedBodySize > 0 && entry.transferSize === 0) {
    return { fromCache: true, receivedBytes: 0 };
  }
  // 크로스오리진 opaque(size 전부 0): TAO 없으면 캐시/네트워크 구분이 어렵다.
  if (entry.encodedBodySize === 0 && entry.decodedBodySize === 0 && entry.transferSize === 0) {
    const span = resourceTimingSpanMs(entry);
    // 메모리/디스크 캐시는 대개 매우 짧다(이전 12ms는 너무 타이트해 캐시가 네트워크로 잡힘)
    if (span >= 0 && span < 40) {
      return { fromCache: true, receivedBytes: 0 };
    }
    if (cl > 0) return { fromCache: false, receivedBytes: cl };
    return { fromCache: false, receivedBytes: estimateGzipWireBytesFromDecodedBody(body) };
  }
  if (cl > 0) return { fromCache: false, receivedBytes: cl };
  return { fromCache: false, receivedBytes: estimateGzipWireBytesFromDecodedBody(body) };
}

/** Resource Timing 항목이 늦게 붙는 경우를 위해 짧게 재시도 */
async function resolveFetchNetworkTransfer(
  resourceUrl: string,
  responseBodyBytes: number,
  contentLengthBytes: number
): Promise<{ fromCache: boolean; receivedBytes: number }> {
  const delaysMs = [0, 16, 48, 120];
  let lastEntry: ResourceTimingEntry | null = null;
  for (const delay of delaysMs) {
    if (delay > 0) await sleepMs(delay);
    const entry = findLatestFetchResourceTiming(resourceUrl);
    if (!entry) continue;
    lastEntry = entry;
    const classified = classifyFetchNetworkTransfer(entry, responseBodyBytes, contentLengthBytes);
    // 캐시이거나 transferSize/본문 크기가 확정되면 즉시 반환
    if (classified.fromCache || entry.transferSize > 0 || entry.decodedBodySize > 0) {
      return classified;
    }
    // opaque면 한 번 더 기다려 transferSize가 채워지는지 확인
  }
  return classifyFetchNetworkTransfer(lastEntry, responseBodyBytes, contentLengthBytes);
}

function getSupabaseUrlPrefix(): string {
  return String(process.env.REACT_APP_SUPABASE_URL ?? '')
    .trim()
    .replace(/\/+$/, '');
}

/** Supabase 프로젝트 호스트(또는 rest/auth/storage/functions 경로) */
function isSupabaseRequestUrl(urlStr: string): boolean {
  const raw = String(urlStr ?? '').trim();
  if (!raw) return false;
  const prefix = getSupabaseUrlPrefix();
  if (prefix) {
    try {
      if (raw === prefix || raw.startsWith(`${prefix}/`)) return true;
    } catch {
      /* fall through */
    }
  }
  try {
    const u = new URL(raw, typeof window !== 'undefined' ? window.location.href : 'http://local');
    return /\/(rest|auth|storage|functions)\/v1(?:\/|$)/.test(u.pathname);
  } catch {
    return /\/(rest|auth|storage|functions)\/v1(?:\/|$|\?)/.test(raw);
  }
}

/** TopNav DB egress 추정 대상 — PostgREST만 */
function isSupabaseRestUrl(urlStr: string): boolean {
  const raw = String(urlStr ?? '').trim();
  if (!raw || !isSupabaseRequestUrl(raw)) return false;
  try {
    const u = new URL(raw, typeof window !== 'undefined' ? window.location.href : 'http://local');
    return /\/rest\/v1(?:\/|$)/.test(u.pathname);
  } catch {
    return /\/rest\/v1(?:\/|$|\?)/.test(raw);
  }
}

function parseRestTable(urlStr: string): string | null {
  try {
    const u = new URL(urlStr, 'http://local');
    const m = u.pathname.match(/\/rest\/v1\/([^/?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function countJsonRows(raw: string): number | null {
  const t = raw.trim();
  if (!t.startsWith('[')) return null;
  try {
    const v = JSON.parse(t) as unknown;
    return Array.isArray(v) ? v.length : null;
  } catch {
    return null;
  }
}

/** 단일 객체 upsert/PATCH도 1행으로 침 */
function countUpsertPayloadRows(raw: string): number {
  const t = raw.trim();
  if (!t) return 0;
  const arr = countJsonRows(t);
  if (arr != null) return arr;
  if (t.startsWith('{')) {
    try {
      JSON.parse(t);
      return 1;
    } catch {
      return 0;
    }
  }
  return 0;
}

async function readRequestBodyBytes(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<{ text: string | null; bytes: number }> {
  try {
    if (typeof input === 'object' && input != null && !(input instanceof URL) && 'url' in input) {
      const req = input as Request;
      const c = req.clone();
      const text = await c.text();
      return { text: text || null, bytes: new TextEncoder().encode(text).length };
    }
    const b = init?.body;
    if (b == null) return { text: null, bytes: 0 };
    if (typeof b === 'string') return { text: b, bytes: new TextEncoder().encode(b).length };
    if (b instanceof URLSearchParams) {
      const s = b.toString();
      return { text: s, bytes: new TextEncoder().encode(s).length };
    }
    if (b instanceof Blob) {
      const text = await b.text();
      return { text, bytes: new TextEncoder().encode(text).length };
    }
  } catch {
    return { text: null, bytes: 0 };
  }
  return { text: null, bytes: 0 };
}

let origFetch: typeof fetch | null = null;
let installed = false;

export function installDevNetworkTransferMonitor(): () => void {
  if (typeof window === 'undefined' || installed) {
    return () => {};
  }
  dailyUsage = loadDailyUsageStore();
  installed = true;
  const uninstallCrossTab = installCrossTabUsageSync();
  origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const urlStr =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    const isRest = isSupabaseRestUrl(urlStr);
    let restTable: string | null = null;
    try {
      restTable = isRest ? parseRestTable(urlStr) : null;
    } catch {
      restTable = null;
    }
    const method = (
      init?.method ??
      (typeof input === 'object' && input != null && !(input instanceof URL) && 'method' in input
        ? (input as Request).method
        : undefined) ??
      'GET'
    ).toUpperCase();

    // TopNav DB egress 추정은 PostgREST만. Auth/Storage 등은 패스스루.
    if (!isRest) {
      return origFetch!(input, init);
    }

    const { text: reqText, bytes: reqBodyBytes } = await readRequestBodyBytes(input, init);
    const res = await origFetch!(input, init);

    // 응답 본문은 캐시 여부·행 수 집계용. 「받음」은 transferSize → Content-Length → gzip 추정 순.
    const resClone = res.clone();
    const contentLengthBytes = parseContentLengthBytes(res.headers);
    void (async () => {
      let responseBodyBytes = 0;
      let responseText = '';
      try {
        responseText = await resClone.text();
        responseBodyBytes = new TextEncoder().encode(responseText).length;
      } catch {
        responseBodyBytes = 0;
        responseText = '';
      }

      if (!res.ok) {
        try {
          console.error('[dev-network] REST error response', {
            status: res.status,
            url: urlStr,
            method,
            table: restTable,
            body: responseText,
          });
        } catch {
          /* ignore */
        }
      }

      const transfer = await resolveFetchNetworkTransfer(urlStr, responseBodyBytes, contentLengthBytes);
      if (transfer.fromCache) {
        snapshot.browserHttpCacheRestCount += 1;
        bumpBurst((b) => {
          b.httpCacheRest += 1;
        });
        notify();
        return;
      }

      snapshot.fetchCount += 1;
      snapshot.restV1Count += 1;
      bumpBurst((b) => {
        b.fetchCount += 1;
        b.restV1Count += 1;
      });

      if (reqBodyBytes > 0) {
        snapshot.requestBytes += reqBodyBytes;
        addDailySent(reqBodyBytes);
        bumpBurst((b) => {
          b.requestBytes += reqBodyBytes;
        });
      }

      const receivedBytes = transfer.receivedBytes;
      if (receivedBytes > 0) {
        snapshot.responseBytes += receivedBytes;
        addDailyReceived(receivedBytes);
        bumpBurst((b) => {
          b.responseBytes += receivedBytes;
        });
      }

      const programMonitoredTable =
        restTable != null && PROGRAM_REST_TABLES.has(restTable) ? restTable : null;
      if (
        programMonitoredTable &&
        reqText &&
        (method === 'POST' || method === 'PATCH' || method === 'PUT')
      ) {
        const sent = countUpsertPayloadRows(reqText);
        if (sent > 0) {
          snapshot.programUpsertPayloadRows += sent;
          snapshot.programUpsertCalls += 1;
          const denom = snapshot.lastProgramGetRowsByTable[programMonitoredTable] ?? null;
          const upsertSnap = {
            table: programMonitoredTable,
            sentRows: sent,
            denomRows: denom,
          };
          snapshot.lastProgramUpsert = upsertSnap;
          bumpBurst((b) => {
            b.programUpsertPayloadRows += sent;
            b.programUpsertCalls += 1;
            b.lastProgramUpsert = upsertSnap;
          });
        }
      }

      if (programMonitoredTable && method === 'GET' && responseText) {
        const n = countJsonRows(responseText);
        if (n != null) {
          snapshot.programGetPayloadRows += n;
          snapshot.programGetCalls += 1;
          snapshot.lastProgramGetRowsByTable[programMonitoredTable] = n;
          bumpBurst((b) => {
            b.programGetPayloadRows += n;
            b.programGetCalls += 1;
          });
        }
      }

      notify();
    })();

    return res;
  };

  return () => {
    uninstallCrossTab();
    if (origFetch) window.fetch = origFetch;
    origFetch = null;
    installed = false;
    // 절대 resetDevNetworkTransferStats 호출 금지 — Strict Mode/탭 종료 시
    // 공유 LS를 0으로 덮어 메인 TopNav 누적이 날아감
    clearSessionTransferSnapshotOnly();
  };
}
